'use client';

import { useEffect, useRef } from 'react';
import useVelocityAccountStore from 'src/stores/useVelocityAccountsStore';
import useNotificationStore from 'src/stores/useNotificationStore';
import { notify } from 'src/utils/notifications';
import { useLowHealthWarnings } from 'src/hooks/useLowHealthWarning';

const LOW_HEALTH_THRESHOLD_PERCENT = 20;

/**
 * Fires a warning toast when account health drops below the threshold, for both
 * cross accounts and isolated positions across all subaccounts. Toasts stay up
 * until dismissed, and each warning only toasts once while it remains live.
 */
const useLiquidationWarningToasts = () => {
	const currentUserKey = useVelocityAccountStore((s) => s.currentUserKey);
	const accountsMap = useVelocityAccountStore((s) => s.accounts);
	const removeNotificationById = useNotificationStore(
		(s) => s.removeNotificationById
	);

	const warnings = useLowHealthWarnings(LOW_HEALTH_THRESHOLD_PERCENT, true);

	// Toasted keys and the copy each was last shown with, held only while the
	// warning is live. Survives the user dismissing the toast, so the same
	// warning isn't re-shown.
	const shownWarningsRef = useRef<Map<string, string>>(new Map());

	useEffect(() => {
		const liveKeys = new Set<string>();

		warnings.forEach((warning) => {
			const key =
				warning.type === 'cross'
					? `warning_cross_${warning.userKey}`
					: `warning_iso_${warning.userKey}_${warning.marketIndex}`;

			liveKeys.add(key);

			const isCurrentAccount = warning.userKey === currentUserKey;
			const accountName = accountsMap[warning.userKey]?.name ?? 'Account';

			// Qualify with the account name only when it isn't the one on screen.
			let description: string;

			if (warning.type === 'cross') {
				description = isCurrentAccount
					? `Your Cross Position's Health is below ${LOW_HEALTH_THRESHOLD_PERCENT}% and may face liquidation soon`
					: `Your account (${accountName})'s Health is below ${LOW_HEALTH_THRESHOLD_PERCENT}% and may face liquidation soon`;
			} else {
				description = isCurrentAccount
					? `${warning.marketLabel} (Isolated) Health is below ${LOW_HEALTH_THRESHOLD_PERCENT}% and may face liquidation soon`
					: `Your account (${accountName})'s ${warning.marketLabel} (Isolated) Health is below ${LOW_HEALTH_THRESHOLD_PERCENT}% and may face liquidation soon`;
			}

			const shownDescription = shownWarningsRef.current.get(key);

			if (shownDescription === description) return;

			notify({
				type: 'warning',
				id: key,
				message: 'LOW HEALTH WARNING',
				description,
				showUntilCancelled: true,
				updatePrevious: true,
				// A toast already shown is being re-worded, not re-raised: switching
				// subaccounts changes which of them the copy has to name. Refuse to
				// add it back if it's gone, so re-wording can't undo a Clear All.
				onlyShowIfPreviousTarget: shownDescription !== undefined,
			});

			shownWarningsRef.current.set(key, description);
		});

		// Nothing else clears a showUntilCancelled toast, so a warning that has
		// gone away has to take its toast down here — otherwise it claims the
		// account is at risk for the life of the page.
		shownWarningsRef.current.forEach((_description, key) => {
			if (!liveKeys.has(key)) {
				removeNotificationById(key);
				shownWarningsRef.current.delete(key);
			}
		});
	}, [warnings, currentUserKey, accountsMap, removeNotificationById]);

	// Unmount: toasts outlive this hook, so don't orphan them on screen.
	useEffect(() => {
		const shownWarnings = shownWarningsRef.current;

		return () => {
			shownWarnings.forEach((_description, key) => removeNotificationById(key));
			shownWarnings.clear();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
};

export default useLiquidationWarningToasts;
